import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Mail } from 'lucide-react';
import footerService, { FooterSection } from '../services/footerService';

// ============================================
// Footer – Port des Flutter AppFooter.
// Lädt Sektionen dynamisch (GET /api/footer/sections), mit Fallback-Sektionen
// falls das Backend nicht erreichbar ist. Responsive (Desktop/Mobile).
// ============================================

const FALLBACK_SECTIONS: FooterSection[] = [
  {
    id: '1',
    title: 'Entdecken',
    order: 0,
    isVisible: true,
    items: [
      { id: '1', label: 'Turniere', route: '/turniere', order: 0, isVisible: true },
      { id: '2', label: 'Vereine', route: '/vereine', order: 1, isVisible: true },
      { id: '3', label: 'Spielstätten', route: '/spielstaetten', order: 2, isVisible: true },
      { id: '4', label: 'Shops', route: '/shops', order: 3, isVisible: true },
      { id: '5', label: 'Turnierserien', route: '/turnierserien', order: 4, isVisible: true },
    ],
  },
  {
    id: '2',
    title: 'Rechtliches',
    order: 1,
    isVisible: true,
    items: [
      { id: '1', label: 'Impressum', route: '/dartcloud/impressum', order: 0, isVisible: true },
      { id: '2', label: 'Datenschutz', route: '/dartcloud/datenschutz', order: 1, isVisible: true },
      { id: '3', label: 'AGB', route: '/dartcloud/agb', order: 2, isVisible: true },
      { id: '4', label: 'Cookie-Richtlinie', route: '/dartcloud/cookie-richtlinie', order: 3, isVisible: true },
    ],
  },
  {
    id: '3',
    title: 'Über DartCloud',
    order: 2,
    isVisible: true,
    items: [
      { id: '1', label: 'Über uns', route: '/info', order: 0, isVisible: true },
      { id: '2', label: 'Forum', route: '/forum', order: 1, isVisible: true },
      { id: '3', label: 'Kontakt', route: '/kontakt', order: 2, isVisible: true },
      { id: '4', label: 'Community', route: '/community', order: 3, isVisible: true },
    ],
  },
];

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState<FooterSection[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    footerService.getVisibleSections().then((data) => {
      if (!cancelled) setSections(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const effective = (sections ?? FALLBACK_SECTIONS)
    .filter((s) => s.isVisible)
    .sort((a, b) => a.order - b.order);

  const handleLink = (route: string) => {
    if (!route) return;
    if (route.startsWith('http://') || route.startsWith('https://') || route.startsWith('mailto:')) {
      window.open(route, '_blank', 'noopener,noreferrer');
    } else {
      navigate(route);
    }
  };

  const renderColumn = (section: FooterSection) => (
    <div key={section.id} className="min-w-0">
      <h3 className="mb-4 text-[15px] font-semibold text-white">{section.title}</h3>
      <ul className="space-y-3">
        {section.items
          .filter((i) => i.isVisible)
          .sort((a, b) => a.order - b.order)
          .map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => handleLink(item.route)}
                className="text-left text-sm text-gray-400 transition-colors hover:text-white"
              >
                {item.label}
              </button>
            </li>
          ))}
      </ul>
    </div>
  );

  const logo = (
    <button type="button" onClick={() => navigate('/')} aria-label="Zur Startseite">
      <img
        src="/DC_Logo_Typo_Blau_gross.png"
        alt="DartCloud"
        className="h-8 w-auto object-contain"
      />
    </button>
  );

  const description = (
    <p className="text-sm leading-relaxed text-gray-400">
      Die Suchmaschine des Dartsports.
      <br />
      Finde Turniere, Vereine, Spielstätten und mehr.
    </p>
  );

  const social = (
    <div className="flex items-center gap-3">
      <button
        type="button"
        aria-label="Instagram"
        onClick={() => handleLink('https://instagram.com/dartcloudapp')}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-800 text-gray-400 transition-colors hover:bg-zinc-700 hover:text-white"
      >
        <Globe size={18} />
      </button>
      <button
        type="button"
        aria-label="E-Mail"
        onClick={() => handleLink('mailto:info@dartcloudapp.de')}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-800 text-gray-400 transition-colors hover:bg-zinc-700 hover:text-white"
      >
        <Mail size={18} />
      </button>
    </div>
  );

  const copyright = (
    <p className="text-[13px] text-gray-600">
      © {new Date().getFullYear()} DartCloud. Alle Rechte vorbehalten.
    </p>
  );

  return (
    <footer className="border-t border-zinc-800 bg-black">
      <div className="mx-auto max-w-7xl px-6 py-12 sm:px-8 lg:py-14">
        {/* Oberer Bereich */}
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[2fr_repeat(3,1fr)]">
          <div>
            {logo}
            <div className="mt-4">{description}</div>
          </div>
          {effective.map(renderColumn)}
        </div>

        {/* Divider */}
        <div className="my-8 h-px bg-zinc-800" />

        {/* Unterer Bereich */}
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          {copyright}
          {social}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
